import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

// Material UI Icons
import CategoryIcon from '@mui/icons-material/Category';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';

import { useCategory } from '../context/CategoryContext';
import Button from '../components/formElements/Button';

interface CategoryFormData {
  name: string;
  image?: File;
}

interface CategoryFormProps {
  parentCategoryId?: number;
  id?: number;
  name?: string;
  image?: string;
  inEditMode?: boolean;
}

function CategoryForm({
  parentCategoryId,
  id,
  name = '',
  image = '',
  inEditMode = false,
}: CategoryFormProps) {
  const [previewImage, setPreviewImage] = useState<string | null>(
    image || null,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CategoryFormData>({
    defaultValues: {
      name,
    },
  });

  const { createCategory, editCategory } = useCategory();
  const navigate = useNavigate();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);

      // Podgląd obrazu
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: CategoryFormData) => {
    setIsSubmitting(true);

    const toastId = toast.loading(
      inEditMode ? 'Edytowanie kategorii...' : 'Dodawanie kategorii...',
      {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
        theme: 'colored',
      },
    );

    try {
      const formData = new FormData();
      formData.append('name', data.name);

      if (uploadedFile) {
        formData.append('image', uploadedFile);
      }

      if (inEditMode && id) {
        await editCategory(id, data.name);
        toast.update(toastId, {
          render: 'Kategoria została zaktualizowana pomyślnie!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        // Nawigacja z powrotem do kategorii nadrzędnej lub listy głównej
        if (parentCategoryId) {
          navigate(`/admin/kategorie/${parentCategoryId}`);
        } else {
          navigate('/admin/kategorie');
        }
      } else {
        await createCategory(data.name, parentCategoryId);
        toast.update(toastId, {
          render: 'Kategoria została dodana pomyślnie!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        // Nawigacja z powrotem do kategorii nadrzędnej lub listy głównej
        if (parentCategoryId) {
          navigate(`/admin/kategorie/${parentCategoryId}`);
        } else {
          navigate('/admin/kategorie');
        }
      }
    } catch (error: any) {
      console.error('Błąd podczas zapisywania kategorii:', error);
      toast.update(toastId, {
        render:
          error.message ||
          (inEditMode
            ? 'Nie udało się zaktualizować kategorii'
            : 'Nie udało się dodać kategorii'),
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-200 flex items-center gap-2">
          <CategoryIcon className="text-primary" />
          {inEditMode ? 'Edycja kategorii' : 'Dodaj nową kategorię'}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pierwsza kolumna - Zdjęcia (wyłączone) */}
          <div className="lg:col-span-1">
            <h3 className="font-medium text-gray-900 mb-3">
              Zdjęcie kategorii
            </h3>

            <div className="mb-6">
              <div className="relative">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center cursor-not-allowed bg-gray-50 overflow-hidden">
                  <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
                    <div className="bg-white bg-opacity-80 px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
                      <InfoIcon className="text-amber-500" fontSize="small" />
                      <span className="text-sm text-gray-600 font-medium">
                        Funkcja tymczasowo niedostępna
                      </span>
                    </div>
                  </div>

                  {previewImage ? (
                    <div className="relative">
                      <img
                        src={previewImage}
                        alt="Zdjęcie kategorii"
                        className="mx-auto max-h-48 object-contain opacity-50"
                      />
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CloudUploadIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">
                        Dodawanie zdjęć kategorii
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        PNG, JPG do 1MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-2 italic">
                Dodawanie zdjęć kategorii będzie dostępne w przyszłej
                aktualizacji.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <h4 className="text-sm font-medium text-blue-700 mb-2">
                Wskazówki:
              </h4>
              <ul className="text-sm text-blue-600 list-disc pl-5 space-y-1">
                <li>
                  Dobrze nazwane kategorie ułatwiają klientom znalezienie
                  produktów
                </li>
                <li>
                  Unikaj zbyt ogólnych lub zbyt szczegółowych nazw kategorii
                </li>
                <li>Kategorie można zagnieżdżać tworząc logiczną strukturę</li>
                <li>Pamiętaj o SEO przy tworzeniu nazw kategorii</li>
              </ul>
            </div>
          </div>

          {/* Druga kolumna - Dane podstawowe */}
          <div className="lg:col-span-2">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Informacje podstawowe
              </h3>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nazwa kategorii *
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: 'Nazwa kategorii jest wymagana',
                      minLength: {
                        value: 2,
                        message: 'Nazwa musi mieć co najmniej 2 znaki',
                      },
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="name"
                        className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                        placeholder="Np. Elektronika, Odzież, RTV"
                      />
                    )}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {String(errors.name.message)}
                    </p>
                  )}
                </div>

                {parentCategoryId && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InfoIcon className="text-yellow-600 mr-2" />
                      <p className="text-sm text-yellow-800">
                        Tworzysz <strong>podkategorię</strong>. Będzie ona
                        widoczna wewnątrz kategorii nadrzędnej.
                      </p>
                    </div>
                  </div>
                )}

                {/* Informacja o relacji kategorii */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Struktura kategorii:
                  </h4>
                  <div className="flex items-center">
                    <div className="flex items-center space-x-2">
                      <CategoryIcon
                        fontSize="small"
                        className="text-gray-500"
                      />
                      <span className="text-sm text-gray-700">
                        {parentCategoryId
                          ? `Podkategoria w: [Kategoria nadrzędna]`
                          : 'Kategoria główna'}
                      </span>
                    </div>
                    {parentCategoryId && (
                      <Button
                        type="button"
                        colorTheme="outline"
                        size="xs"
                        className="ml-auto w-auto"
                        onClick={() => navigate('/admin/kategorie')}
                      >
                        <ArrowBackIcon fontSize="small" className="mr-1" />
                        Wróć do kategorii głównych
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dodatkowa przestrzeń dla przyszłych pól */}
            <div className="h-12 md:h-24 lg:h-32"></div>
          </div>
        </div>

        {/* Przyciski formularza */}
        <div className="mt-8 flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button
            type="button"
            colorTheme="outline"
            onClick={() =>
              parentCategoryId
                ? navigate(`/admin/kategorie/${parentCategoryId}`)
                : navigate('/admin/kategorie')
            }
            className="w-auto px-6"
          >
            Anuluj
          </Button>
          <Button
            type="submit"
            colorTheme="primary"
            className="w-auto px-6"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? inEditMode
                ? 'Aktualizowanie...'
                : 'Tworzenie...'
              : inEditMode
                ? 'Zaktualizuj kategorię'
                : 'Utwórz kategorię'}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}

export default CategoryForm;
